import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

export default function StudentFeeInfo() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const parts = window.location.pathname.split('/');
    const idx = parts.indexOf('student-info');
    if (idx === -1 || parts.length < idx + 3) {
      setError('Invalid URL');
      setLoading(false);
      return;
    }
    const schoolCode = decodeURIComponent(parts[idx + 1]);
    const studentId  = parts[idx + 2];
    const params = new URLSearchParams(window.location.search);
    const year = params.get('year') || '';

    axios.get(`${API}/student-info/${encodeURIComponent(schoolCode)}/${studentId}${year ? `?year=${year}` : ''}`)
      .then(res => {
        if (res.data.success) setData(res.data.data);
        else setError(res.data.message || 'Failed to load');
      })
      .catch(err => setError(err.response?.data?.message || 'Failed to load student info'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={s.center}>
      <style>{`@keyframes fi-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ width: 40, height: 40, border: '4px solid #e0e0e0', borderTop: '4px solid #1a237e', borderRadius: '50%', animation: 'fi-spin 1s linear infinite' }} />
      <p style={{ marginTop: 12, color: '#666' }}>Loading...</p>
    </div>
  );
  if (error) return <div style={s.center}><p style={{ color: '#e74c3c', fontSize: 18 }}>❌ {error}</p></div>;
  if (!data) return null;

  const { student, school_name, academic_year, payments, installments } = data;

  const fmt = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

  const tuitionFee = parseFloat(student.total_fee) || 0;
  const busFee = parseFloat(student.bus_fee) || 0;
  const uniformFee = parseFloat(student.uniform_fee) || 0;
  const booksFee = parseFloat(student.books_fee) || 0;
  const grossTotal = tuitionFee + busFee + uniformFee + booksFee;
  const paidAmount = parseFloat(student.paid_amount) || 0;
  const oldDue = parseFloat(student.old_due_amount) || 0;
  const concession = parseFloat(student.concession_amount) || 0;
  const pending = parseFloat(student.pending_amount) || 0;

  const paymentLabel = (t) => {
    const map = { monthly: 'Monthly', term: 'Term', old_due: 'Old Due', admission: 'Admission', tuition: 'Tuition Fee', bus_fee: 'Transport Fee', uniform_fee: 'Uniform Fee', books_fee: 'Books Fee' };
    return map[t] || t;
  };

  // ── Monthly overdue breakdown ────────────────────────────────────────────────
  const MONTHS_SHORT = ['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May'];
  const MONTH_NUM    = { Jan:1, Feb:2, Mar:3, Apr:4, May:5, Jun:6, Jul:7, Aug:8, Sep:9, Oct:10, Nov:11, Dec:12 };

  const getMonthlyBreakdown = () => {
    const yearMatch = String(academic_year || '').match(/^(\d{4})/);
    if (!yearMatch) return [];
    const startYear = parseInt(yearMatch[1], 10);
    const monthlyPaid = (payments || []).filter(p => p.payment_type === 'monthly');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return MONTHS_SHORT.map((mon, idx) => {
      const year = idx <= 6 ? startYear : startYear + 1; // Jun-Dec = start year, Jan-May = next year
      const label = `${mon} ${year}`;
      const paid = monthlyPaid.some(p => {
        const my = (p.month_year || '').toLowerCase();
        return my.includes(mon.toLowerCase()) && my.includes(String(year));
      });
      const monthDate = new Date(year, MONTH_NUM[mon] - 1, 1);
      const isOverdue = !paid && monthDate <= today;
      const isFuture  = !paid && monthDate > today;
      return { label, paid, isOverdue, isFuture };
    });
  };

  const monthlyBreakdown = getMonthlyBreakdown();
  const overdueMonths = monthlyBreakdown.filter(m => m.isOverdue);

  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* Header */}
        <div style={s.header}>
          <h1 style={s.schoolName}>{school_name}</h1>
          {student.school_tagline && <p style={s.tagline}>{student.school_tagline}</p>}
          {student.school_location && <p style={s.location}>📍 {student.school_location}</p>}
          <h2 style={s.title}>STUDENT FEE DETAILS</h2>
          <p style={s.yearBadge}>Academic Year: {academic_year}</p>
        </div>

        {/* Student Info */}
        <div style={s.section}>
          <div style={s.infoGrid}>
            <div style={s.infoItem}><span style={s.label}>Student Name</span><span style={s.val}>{student.student_name}</span></div>
            <div style={s.infoItem}><span style={s.label}>Father's Name</span><span style={s.val}>{student.father_name}</span></div>
            <div style={s.infoItem}><span style={s.label}>Class</span><span style={s.val}>{student.class}</span></div>
            {student.admission_no && <div style={s.infoItem}><span style={s.label}>Admission No</span><span style={s.val}>{student.admission_no}</span></div>}
            {student.village && <div style={s.infoItem}><span style={s.label}>Village</span><span style={s.val}>{student.village}</span></div>}
          </div>
        </div>

        {/* Fee Breakup */}
        <div style={s.sectionHeader}>💰 Fee Breakup</div>
        <div style={s.section}>
          <table style={s.table}>
            <tbody>
              <tr><td style={s.tdLabel}>Tuition Fee</td><td style={s.tdVal}>{fmt(tuitionFee)}</td></tr>
              {busFee > 0 && <tr><td style={s.tdLabel}>Transport Fee</td><td style={s.tdVal}>{fmt(busFee)}</td></tr>}
              {uniformFee > 0 && <tr><td style={s.tdLabel}>Uniform Fee</td><td style={s.tdVal}>{fmt(uniformFee)}</td></tr>}
              {booksFee > 0 && <tr><td style={s.tdLabel}>Books Fee</td><td style={s.tdVal}>{fmt(booksFee)}</td></tr>}
              <tr style={{ background: '#e8eaf6' }}>
                <td style={{ ...s.tdLabel, fontWeight: 700, color: '#1a237e' }}>Gross Total</td>
                <td style={{ ...s.tdVal, fontWeight: 700, color: '#1a237e', fontSize: 16 }}>{fmt(grossTotal)}</td>
              </tr>
              {concession > 0 && <tr><td style={s.tdLabel}>Concession</td><td style={{ ...s.tdVal, color: '#8e24aa' }}>- {fmt(concession)}</td></tr>}
              {oldDue > 0 && <tr><td style={s.tdLabel}>Old Due (Previous Year)</td><td style={{ ...s.tdVal, color: '#e67e22' }}>+ {fmt(oldDue)}</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Payment Summary */}
        <div style={s.sectionHeader}>📊 Payment Summary</div>
        <div style={{ display: 'flex', gap: 12, padding: '16px 20px', flexWrap: 'wrap' }}>
          <div style={s.summaryCard('#e8f5e9', '#2e7d32')}>
            <span style={s.summaryLabel}>Total Paid</span>
            <span style={{ fontSize: 22, fontWeight: 800, color: '#2e7d32' }}>{fmt(paidAmount)}</span>
          </div>
          <div style={s.summaryCard(pending > 0 ? '#fff3e0' : '#e8f5e9', pending > 0 ? '#e65100' : '#2e7d32')}>
            <span style={s.summaryLabel}>Pending</span>
            <span style={{ fontSize: 22, fontWeight: 800, color: pending > 0 ? '#e65100' : '#2e7d32' }}>{fmt(pending)}</span>
          </div>
        </div>

        {/* Installments */}
        {installments && installments.length > 0 && (
          <>
            <div style={s.sectionHeader}>📅 Installment Schedule</div>
            <div style={s.section}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Term</th>
                    <th style={s.th}>Due Date</th>
                    <th style={s.th}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {installments.map((inst, i) => {
                    const isOverdue = inst.due_date && new Date(inst.due_date) < new Date();
                    return (
                      <tr key={i} style={isOverdue ? { background: '#fff8e1' } : {}}>
                        <td style={s.td}>{inst.term_name}</td>
                        <td style={s.td}>{inst.due_date ? new Date(inst.due_date).toLocaleDateString('en-IN') : '—'}</td>
                        <td style={{ ...s.td, fontWeight: 600 }}>{fmt(inst.expected_amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Payment History */}
        {payments && payments.length > 0 && (
          <>
            <div style={s.sectionHeader}>📋 Payment History</div>
            <div style={s.section}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Date</th>
                    <th style={s.th}>Type</th>
                    <th style={s.th}>Amount</th>
                    <th style={s.th}>Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.payment_id}>
                      <td style={s.td}>{new Date(p.payment_date).toLocaleDateString('en-IN')}</td>
                      <td style={s.td}>{paymentLabel(p.payment_type)}</td>
                      <td style={{ ...s.td, color: '#2e7d32', fontWeight: 700 }}>{fmt(p.amount)}</td>
                      <td style={{ ...s.td, fontFamily: 'monospace', color: '#1a56db', fontSize: 12 }}>{p.receipt_no || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#e8f5e9' }}>
                    <td style={{ ...s.td, fontWeight: 700 }} colSpan={2}>Total Paid</td>
                    <td style={{ ...s.td, fontWeight: 700, color: '#2e7d32' }} colSpan={2}>
                      {fmt(payments.reduce((sum, p) => sum + parseFloat(p.amount), 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}

        {/* Monthly Payment Status */}
        {monthlyBreakdown.length > 0 && (
          <>
            {overdueMonths.length > 0 && (
              <div style={{ background: '#ffebee', border: '2px solid #e53935', borderRadius: 8, margin: '0 20px 4px', padding: '12px 16px' }}>
                <div style={{ color: '#b71c1c', fontWeight: 700, fontSize: 15 }}>
                  ⚠️ OVERDUE — {overdueMonths.length} Month{overdueMonths.length > 1 ? 's' : ''} Unpaid
                </div>
                <div style={{ color: '#c62828', fontSize: 13, marginTop: 4 }}>
                  {overdueMonths.map(m => m.label).join(', ')} — Monthly fee not received
                </div>
              </div>
            )}
            <div style={s.sectionHeader}>📅 Monthly Payment Status</div>
            <div style={{ padding: '14px 20px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {monthlyBreakdown.map((m, i) => (
                  <div key={i} style={{
                    padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    textAlign: 'center', minWidth: 72,
                    background: m.paid ? '#e8f5e9' : m.isOverdue ? '#ffebee' : '#f5f5f5',
                    color:      m.paid ? '#2e7d32' : m.isOverdue ? '#b71c1c' : '#9e9e9e',
                    border:     `1.5px solid ${m.paid ? '#a5d6a7' : m.isOverdue ? '#ef9a9a' : '#e0e0e0'}`,
                  }}>
                    <div>{m.label}</div>
                    <div style={{ fontSize: 10, marginTop: 2 }}>
                      {m.paid ? '✓ Paid' : m.isOverdue ? '✗ Overdue' : '○ Due'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Footer */}
        <div style={{ padding: '16px 20px', borderTop: '2px solid #e8eaf6', textAlign: 'center', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ color: '#888', fontSize: 12, margin: 0 }}>This is a digitally generated fee statement.</p>
          <button
            onClick={() => window.print()}
            style={{ background: '#1a237e', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', cursor: 'pointer', fontSize: 14 }}
          >🖨️ Print</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f0f2f5', padding: '20px 10px', fontFamily: "'Segoe UI', sans-serif" },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  card: { maxWidth: 600, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' },
  header: { background: 'linear-gradient(135deg, #1a237e, #283593)', color: '#fff', padding: '24px 20px', textAlign: 'center' },
  schoolName: { margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: 1 },
  tagline: { margin: '4px 0 0', fontSize: 13, opacity: 0.85 },
  location: { margin: '4px 0 0', fontSize: 13, opacity: 0.75 },
  title: { margin: '14px 0 0', fontSize: 16, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: 10 },
  yearBadge: { display: 'inline-block', marginTop: 8, background: 'rgba(255,255,255,0.2)', padding: '4px 16px', borderRadius: 20, fontSize: 14 },
  section: { padding: '16px 20px' },
  sectionHeader: { padding: '10px 20px', background: '#f5f5f5', fontWeight: 700, fontSize: 14, color: '#1a237e', borderTop: '2px solid #e8eaf6', borderBottom: '1px solid #e0e0e0' },
  infoGrid: { display: 'flex', gap: 20, flexWrap: 'wrap' },
  infoItem: { display: 'flex', flexDirection: 'column', minWidth: 120, marginBottom: 8 },
  label: { fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 },
  val: { fontSize: 15, fontWeight: 600, color: '#1a237e' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { background: '#e8eaf6', color: '#1a237e', padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #c5cae9', fontSize: 12, fontWeight: 600 },
  td: { padding: '8px 10px', borderBottom: '1px solid #eee' },
  tdLabel: { padding: '8px 10px', borderBottom: '1px solid #eee', color: '#555' },
  tdVal: { padding: '8px 10px', borderBottom: '1px solid #eee', textAlign: 'right', fontWeight: 600, color: '#333' },
  summaryLabel: { fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  summaryCard: (bg, border) => ({
    flex: '1 1 140px', background: bg, borderRadius: 10, padding: '14px 18px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    border: `2px solid ${border}22`
  }),
};
